import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Clock, Users, DollarSign, Calendar, ChevronRight, ChevronLeft, Check, Plus, Minus, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../config/api';

// PostgreSQL v2 Widget API base
const WIDGET_API = `${API_URL}/v2/widget`;

// Expandable description component for add-ons
const ExpandableDescription = ({ description, maxLength = 80 }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (!description) return null;
  
  const shouldTruncate = description.length > maxLength;
  const displayText = shouldTruncate && !isExpanded 
    ? description.substring(0, maxLength).trim() + '...'
    : description;
  
  return (
    <div className="mt-1">
      <p className="text-sm text-gray-600">
        {displayText}
      </p>
      {shouldTruncate && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          className="text-xs font-medium text-blue-600 hover:text-blue-700 mt-1 flex items-center gap-1"
        >
          {isExpanded ? (
            <>See less <ChevronUp className="w-3 h-3" /></>
          ) : (
            <>See more <ChevronDown className="w-3 h-3" /></>
          )}
        </button>
      )}
    </div>
  );
};

const CustomerBookingWidget = () => {
  const [searchParams] = useSearchParams();
  const orgParam = searchParams.get('org');
  const refParam = searchParams.get('ref'); // Distribution network referral code
  
  // The org param is now the org slug (not ID)
  const orgSlug = orgParam;
  
  // State management
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [orgBranding, setOrgBranding] = useState({
    name: '',
    logoUrl: '',
    primaryColor: '#007AFF',
    showPoweredByBookingFlows: true,
    companyWebsite: ''
  });
  
  // Booking data
  const [bookingData, setBookingData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    emergencyContact: '',
    partySize: 1,
    ticketCount: 1,
    selectedDate: null,
    selectedTime: null,
    customAnswers: {},
    selectedAddOns: [],
    specialRequests: '',
    contactInfoConfirmed: false
  });
  
  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [calendarLoading, setCalendarLoading] = useState(true);  // New: Calendar skeleton state

  useEffect(() => {
    if (orgSlug) {
      loadServices();
    } else {
      toast.error('Organization not specified');
      setLoading(false);
    }
  }, [orgSlug]);

  // Handle URL params for pre-filling (when returning from cancelled booking)
  useEffect(() => {
    if (services.length > 0) {
      const serviceParam = searchParams.get('service');
      const dateParam = searchParams.get('date');
      const timeParam = searchParams.get('time');
      const ticketsParam = searchParams.get('tickets');
      
      // Pre-select service if param exists
      if (serviceParam) {
        const service = services.find(s => s.id === serviceParam);
        if (service) {
          setSelectedService(service);
          setCurrentStep(2); // Skip to step 2
        }
      }
      
      // Pre-fill booking data
      if (dateParam) {
        setBookingData(prev => ({ ...prev, selectedDate: dateParam }));
      }
      if (timeParam) {
        setBookingData(prev => ({ ...prev, selectedTime: timeParam }));
      }
      if (ticketsParam) {
        const count = parseInt(ticketsParam);
        setBookingData(prev => ({ 
          ...prev, 
          ticketCount: count,
          partySize: count 
        }));
      }
    }
  }, [services, searchParams]);

  const loadServices = async () => {
    try {
      // Use PostgreSQL v2 widget config endpoint
      const response = await fetch(`${WIDGET_API}/${orgSlug}/config`);
      const data = await response.json();
      
      // v2 returns services in the config
      setServices(data.services || []);
      
      // Load org branding from v2 response
      if (data.organization) {
        setOrgBranding({
          name: data.organization.name || '',
          logoUrl: data.organization.logo_url || data.organization.logoUrl || '',
          primaryColor: data.organization.primary_color || data.organization.primaryColor || '#007AFF',
          showPoweredByBookingFlows: data.organization.show_powered_by !== false,
          companyWebsite: data.organization.website || ''
        });
      }
      
      setLoading(false);
      setCalendarLoading(false);  // Calendar ready
    } catch (error) {
      console.error('Error loading services:', error);
      // Fallback to legacy endpoint if v2 fails
      try {
        const fallbackResponse = await fetch(`${API_URL}/public/services?org_slug=${orgSlug}`);
        const fallbackData = await fallbackResponse.json();
        setServices(fallbackData.services || []);
        if (fallbackData.organization) {
          setOrgBranding({
            name: fallbackData.organization.name || '',
            logoUrl: fallbackData.organization.logoUrl || '',
            primaryColor: fallbackData.organization.primaryColor || '#007AFF',
            showPoweredByBookingFlows: fallbackData.organization.showPoweredByBookingFlows !== false,
            companyWebsite: fallbackData.organization.website || ''
          });
        }
        setLoading(false);
        setCalendarLoading(false);  // Calendar ready
      } catch (fallbackError) {
        toast.error('Failed to load services');
        setLoading(false);
        setCalendarLoading(false);  // Stop skeleton even on error
      }
    }
  };

  const loadAvailability = async (serviceId, date) => {
    if (!date) return;
    
    setLoadingSlots(true);
    try {
      // Use PostgreSQL v2 widget time-slots endpoint
      const dateStr = date.toISOString().split('T')[0];
      const partySize = bookingData.partySize || 1;
      
      const response = await fetch(
        `${WIDGET_API}/${orgSlug}/services/${serviceId}/time-slots?date=${dateStr}&partySize=${partySize}`
      );
      const data = await response.json();
      
      // v2 returns { slots: [...] }
      setAvailableSlots(data.slots || []);
    } catch (error) {
      console.error('Error loading availability:', error);
      // Fallback to legacy endpoint
      try {
        const fallbackResponse = await fetch(`${API_URL}/public/services/${serviceId}/availability?days=60`);
        const fallbackData = await fallbackResponse.json();
        const dateStr = date.toISOString().split('T')[0];
        const dayData = fallbackData.availableDates?.find(d => d.date === dateStr);
        setAvailableSlots(dayData?.slots || []);
      } catch (fallbackError) {
        toast.error('Failed to load available times');
        setAvailableSlots([]);
      }
    } finally {
      setLoadingSlots(false);
    }
  };

  const selectService = (service) => {
    setSelectedService(service);
    setBookingData(prev => ({
      ...prev,
      partySize: 1,
      ticketCount: 1,
      selectedAddOns: []
    }));
    setCurrentStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDateSelect = (date) => {
    setBookingData(prev => ({ ...prev, selectedDate: date, selectedTime: null }));
    loadAvailability(selectedService.id, date);
  };

  const handleTimeSelect = (time) => {
    setBookingData(prev => ({ ...prev, selectedTime: time }));
  };

  const handleAddOnToggle = (addOn) => {
    setBookingData(prev => {
      const existing = prev.selectedAddOns.find(a => a.id === addOn.id);
      if (existing) {
        return {
          ...prev,
          selectedAddOns: prev.selectedAddOns.filter(a => a.id !== addOn.id)
        };
      } else {
        return {
          ...prev,
          selectedAddOns: [...prev.selectedAddOns, { ...addOn, quantity: 1 }]
        };
      }
    });
  };

  const updateAddOnQuantity = (addOnId, delta) => {
    setBookingData(prev => ({
      ...prev,
      selectedAddOns: prev.selectedAddOns.map(a => 
        a.id === addOnId 
          ? { ...a, quantity: Math.max(1, a.quantity + delta) }
          : a
      )
    }));
  };

  const calculateTotal = () => {
    if (!selectedService) return 0;
    
    let total = selectedService.price;
    
    // If per-ticket mode, multiply by ticket count
    if (selectedService.bookingMode === 'per_ticket') {
      total *= bookingData.ticketCount;
    }
    
    // Add add-ons
    bookingData.selectedAddOns.forEach(addOn => {
      total += addOn.price * addOn.quantity;
    });
    
    return total;
  };

  const calculateFees = () => {
    const subtotal = calculateTotal();
    
    // 3% platform fee
    const platformFee = subtotal * 0.03;
    
    // 2.9% + $0.30 Stripe fee
    const stripeFee = (subtotal * 0.029) + 0.30;
    
    // Total fees
    const totalFees = platformFee + stripeFee;
    
    // Grand total
    const grandTotal = subtotal + totalFees;
    
    return {
      subtotal,
      platformFee,
      stripeFee,
      totalFees,
      grandTotal
    };
  };

  const validateStep = (step) => {
    switch (step) {
      case 2:
        if (!bookingData.customerName?.trim()) {
          toast.error('Please enter your name');
          return false;
        }
        if (!bookingData.customerEmail?.trim()) {
          toast.error('Please enter your email');
          return false;
        }
        if (!bookingData.customerPhone?.trim()) {
          toast.error('Please enter your phone number');
          return false;
        }
        if (!bookingData.contactInfoConfirmed) {
          toast.error('Please confirm your contact information is correct');
          return false;
        }
        if (!bookingData.selectedDate) {
          toast.error('Please select a date');
          return false;
        }
        if (!bookingData.selectedTime) {
          toast.error('Please select a time');
          return false;
        }
        return true;
      
      case 3:
        // Validate custom questions if any required
        const requiredQuestions = selectedService?.customQuestions?.filter(q => q.required) || [];
        for (const question of requiredQuestions) {
          if (!bookingData.customAnswers[question.id]?.trim()) {
            toast.error(`Please answer: ${question.question}`);
            return false;
          }
        }
        return true;
      
      default:
        return true;
    }
  };

  const goToStep = (step) => {
    if (step > currentStep && !validateStep(currentStep)) {
      return;
    }
    setCurrentStep(step);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submitBooking = async () => {
    if (!validateStep(currentStep)) return;
    
    // Prepare payload for PostgreSQL v2 booking endpoint
    const payload = {
      serviceId: selectedService.id,
      customerName: bookingData.customerName,
      customerEmail: bookingData.customerEmail,
      customerPhone: bookingData.customerPhone,
      partySize: selectedService.bookingMode === 'per_ticket' ? bookingData.ticketCount : bookingData.partySize,
      ticketCount: bookingData.ticketCount,
      preferredDate: bookingData.selectedDate.toISOString().split('T')[0],
      preferredTime: bookingData.selectedTime,
      notes: bookingData.specialRequests,
      customAnswers: bookingData.customAnswers,
      addOns: bookingData.selectedAddOns,
      totalAmount: calculateTotal(),
      // Distribution network referral tracking
      partnerCode: refParam || undefined,
    };
    
    try {
      // Use PostgreSQL v2 widget booking endpoint (includes overbooking prevention)
      const response = await fetch(`${WIDGET_API}/${orgSlug}/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const error = await response.json();
        // Handle specific error cases
        if (error.detail?.includes('not available') || error.detail?.includes('conflict')) {
          toast.error('This time slot is no longer available. Please select another time.');
          // Refresh availability
          loadAvailability(selectedService.id, bookingData.selectedDate);
          return;
        }
        throw new Error(error.detail || 'Booking failed');
      }
      
      const result = await response.json();
      
      if (result.checkoutUrl || result.checkout_url) {
        window.location.href = result.checkoutUrl || result.checkout_url;
      } else if (result.booking) {
        toast.success('Booking submitted successfully!');
        // Redirect to confirmation if available
        if (result.booking.confirmation_code) {
          window.location.href = `/booking-confirmation?code=${result.booking.confirmation_code}`;
        }
      } else {
        toast.success('Booking submitted!');
      }
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(error.message || 'Failed to create booking. Please try again.');
    }
  };

  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const days = [];
    const dayNames = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    
    // Day headers
    dayNames.forEach(name => {
      days.push(
        <div key={`header-${name}`} className="text-center text-xs font-semibold text-gray-500 py-2">
          {name}
        </div>
      );
    });
    
    // Empty cells before first day
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} />);
    }
    
    // Calendar days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const isPast = date < today;
      const isSelected = bookingData.selectedDate && 
        date.toDateString() === bookingData.selectedDate.toDateString();
      
      days.push(
        <button
          key={day}
          disabled={isPast}
          onClick={() => !isPast && handleDateSelect(date)}
          className={`
            aspect-square flex items-center justify-center rounded-lg text-sm font-semibold
            transition-all
            ${isPast ? 'text-gray-300 cursor-not-allowed' : 'text-gray-900 cursor-pointer'}
            ${isSelected ? 'text-white' : 'border border-gray-200'}
          `}
          style={isSelected ? { backgroundColor: orgBranding.primaryColor } : isPast ? {} : { ':hover': { backgroundColor: `${orgBranding.primaryColor}10` } }}
        >
          {day}
        </button>
      );
    }
    
    return days;
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center p-4">
        <div className="text-center">
          <div 
            className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4"
            style={{ borderColor: `${orgBranding.primaryColor} transparent transparent transparent` }}
          ></div>
          <p className="text-gray-600">Loading services...</p>
        </div>
      </div>
    );
  }

  // Step 1: Service Selection
  if (currentStep === 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            {orgBranding.logoUrl && (
              <img src={orgBranding.logoUrl} alt={orgBranding.name} className="h-12 mx-auto mb-4" />
            )}
            <h1 className="font-bold text-3xl text-gray-900 mb-2">Select Your Service</h1>
            <p className="text-gray-600">Choose your experience</p>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {services.length === 0 ? (
              <div className="col-span-2 text-center py-16">
                <p className="text-gray-600">No services available</p>
              </div>
            ) : (
              services.map(service => (
                <Card 
                  key={service.id}
                  className="overflow-hidden cursor-pointer hover:shadow-xl transition-all hover:-translate-y-1"
                  onClick={() => selectService(service)}
                >
                  {service.images?.[0] ? (
                    <img 
                      src={service.images[0]} 
                      alt={service.name}
                      className="w-full h-48 object-cover"
                    />
                  ) : (
                    <div className="w-full h-48 bg-gradient-to-br from-slate-700 to-slate-900" />
                  )}
                  
                  <CardContent className="p-6">
                    <h3 className="font-bold text-xl text-gray-900 mb-4">{service.name}</h3>
                    
                    {service.description && (
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">{service.description}</p>
                    )}
                    
                    <div className="grid grid-cols-2 gap-4 mb-5 text-sm">
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Duration</div>
                        <p className="font-semibold text-gray-900">{service.duration} min</p>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                          {service.bookingMode === 'buyout' ? 'Private' : 'Capacity'}
                        </div>
                        <p className="font-semibold text-gray-900">
                          {service.bookingMode === 'buyout' ? 'Buyout' : `Up to ${service.capacity}`}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                      <div className="text-2xl font-bold" style={{ color: orgBranding.primaryColor }}>
                        ${(service.price || service.base_price || 0).toFixed(2)}
                        {service.bookingMode === 'per_ticket' && (
                          <span className="text-sm font-normal text-gray-500">/ticket</span>
                        )}
                      </div>
                      <Button 
                        style={{ backgroundColor: orgBranding.primaryColor }}
                        className="hover:opacity-90"
                      >
                        Select
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </div>
    );
  }

  // Steps 2-4: Booking Flow
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-between max-w-md mx-auto">
            {[1, 2, 3, 4].map(step => (
              <React.Fragment key={step}>
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm
                  ${currentStep >= step ? 'text-white' : 'bg-gray-200 text-gray-600'}
                `}
                style={currentStep >= step ? { backgroundColor: orgBranding.primaryColor } : {}}
                >
                  {currentStep > step ? <Check className="w-5 h-5" /> : step}
                </div>
                {step < 4 && (
                  <div className={`flex-1 h-1 mx-2 ${currentStep > step ? '' : 'bg-gray-200'}`}
                    style={currentStep > step ? { backgroundColor: orgBranding.primaryColor } : {}}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="flex justify-between max-w-md mx-auto mt-2 text-xs text-gray-600">
            <span>Service</span>
            <span>Details</span>
            <span>Questions</span>
            <span>Confirm</span>
          </div>
        </div>

        <Card className="shadow-xl">
          <CardContent className="p-8">
            {/* Step 2: Details & DateTime */}
            {currentStep === 2 && (
              <div>
                {/* Selected Service Summary */}
                <div className="mb-6 pb-6 border-b border-gray-200">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Selected Service</div>
                  <h2 className="font-bold text-2xl text-gray-900 mb-3">{selectedService.name}</h2>
                  <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {selectedService.duration} min
                    </span>
                    <span className="flex items-center gap-1">
                      <DollarSign className="w-4 h-4" />
                      ${(selectedService.price || selectedService.base_price || 0).toFixed(2)}
                      {selectedService.bookingMode === 'per_ticket' && '/ticket'}
                    </span>
                  </div>
                  <button
                    onClick={() => goToStep(1)}
                    className="mt-3 text-sm font-medium hover:underline"
                    style={{ color: orgBranding.primaryColor }}
                  >
                    ← Change Service
                  </button>
                </div>

                {/* Contact Information */}
                <div className="mb-6">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">Your Details</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                      <Input
                        value={bookingData.customerName}
                        onChange={(e) => setBookingData(prev => ({ ...prev, customerName: e.target.value }))}
                        placeholder="John Doe"
                        className="text-base"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                        <Input
                          type="email"
                          value={bookingData.customerEmail}
                          onChange={(e) => setBookingData(prev => ({ ...prev, customerEmail: e.target.value }))}
                          placeholder="john@example.com"
                          className="text-base"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                        <Input
                          type="tel"
                          value={bookingData.customerPhone}
                          onChange={(e) => setBookingData(prev => ({ ...prev, customerPhone: e.target.value }))}
                          placeholder="(555) 123-4567"
                          className="text-base"
                        />
                      </div>
                    </div>
                    
                    {/* Contact Info Confirmation with SMS Consent */}
                    <div 
                      className="rounded-lg p-4 border"
                      style={{ backgroundColor: `${orgBranding.primaryColor}10`, borderColor: `${orgBranding.primaryColor}30` }}
                    >
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={bookingData.contactInfoConfirmed}
                          onChange={(e) => setBookingData(prev => ({ ...prev, contactInfoConfirmed: e.target.checked }))}
                          className="mt-1 w-5 h-5 rounded border-gray-300"
                          style={{ accentColor: orgBranding.primaryColor }}
                          data-testid="contact-sms-consent-checkbox"
                        />
                        <span className="text-sm text-gray-700">
                          <strong className="font-semibold">I confirm this contact information is correct and agree to the{' '}
                            <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: orgBranding.primaryColor }}>Privacy Policy</a>
                            {' '}and{' '}
                            <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: orgBranding.primaryColor }}>Terms of Service</a>.
                          </strong>
                          <br />
                          <span className="text-xs text-gray-500 mt-2 block">
                            By checking this box, I also consent to receive booking confirmations, reminders, and updates via SMS. Msg frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help.
                          </span>
                        </span>
                      </label>
                    </div>
                    
                    {/* Party Size / Ticket Count */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {selectedService.bookingMode === 'per_ticket' ? 'How many tickets? *' : 'Party Size *'}
                      </label>
                      <div className="flex items-center gap-4">
                        <button
                          type="button"
                          onClick={() => {
                            if (selectedService.bookingMode === 'per_ticket') {
                              setBookingData(prev => ({ ...prev, ticketCount: Math.max(1, prev.ticketCount - 1) }));
                            } else {
                              setBookingData(prev => ({ ...prev, partySize: Math.max(1, prev.partySize - 1) }));
                            }
                          }}
                          className="w-12 h-12 rounded-lg border-2 flex items-center justify-center transition-colors"
                          style={{ borderColor: orgBranding.primaryColor, color: orgBranding.primaryColor }}
                          onMouseEnter={(e) => { e.target.style.backgroundColor = `${orgBranding.primaryColor}10`; }}
                          onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; }}
                        >
                          <Minus className="w-5 h-5" />
                        </button>
                        <span className="text-3xl font-bold min-w-[60px] text-center">
                          {selectedService.bookingMode === 'per_ticket' ? bookingData.ticketCount : bookingData.partySize}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            if (selectedService.bookingMode === 'per_ticket') {
                              setBookingData(prev => ({ ...prev, ticketCount: Math.min(selectedService.capacity, prev.ticketCount + 1) }));
                            } else {
                              setBookingData(prev => ({ ...prev, partySize: Math.min(selectedService.capacity, prev.partySize + 1) }));
                            }
                          }}
                          className="w-12 h-12 rounded-lg border-2 flex items-center justify-center transition-colors"
                          style={{ borderColor: orgBranding.primaryColor, color: orgBranding.primaryColor }}
                          onMouseEnter={(e) => { e.target.style.backgroundColor = `${orgBranding.primaryColor}10`; }}
                          onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; }}
                        >
                          <Plus className="w-5 h-5" />
                        </button>
                        <span className="text-sm text-gray-500 ml-2">(max: {selectedService.capacity})</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Date & Time Selection */}
                <div className="mb-6">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">Date & Time</h3>
                  
                  {/* Calendar */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                    {calendarLoading ? (
                      // Calendar Skeleton
                      <div className="animate-pulse">
                        <div className="flex justify-between items-center mb-4">
                          <div className="h-5 bg-gray-200 rounded w-32"></div>
                          <div className="flex gap-2">
                            <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                            <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                          </div>
                        </div>
                        <div className="grid grid-cols-7 gap-1">
                          {/* Day headers skeleton */}
                          {[...Array(7)].map((_, i) => (
                            <div key={`header-skel-${i}`} className="h-6 bg-gray-200 rounded mx-1"></div>
                          ))}
                          {/* Day cells skeleton */}
                          {[...Array(35)].map((_, i) => (
                            <div key={`day-skel-${i}`} className="aspect-square bg-gray-200 rounded-lg"></div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="font-semibold text-gray-900">
                            {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                          </h4>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() - 1)))}
                              className="p-2 hover:bg-gray-200 rounded-lg transition"
                            >
                              <ChevronLeft className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() + 1)))}
                              className="p-2 hover:bg-gray-200 rounded-lg transition"
                            >
                              <ChevronRight className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-7 gap-1">
                          {renderCalendar()}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Time Slots */}
                  {bookingData.selectedDate && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Select Time</h4>
                      {loadingSlots ? (
                        <div className="text-center py-8">
                          <div 
                            className="inline-block w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
                            style={{ borderColor: `${orgBranding.primaryColor} transparent transparent transparent` }}
                          ></div>
                        </div>
                      ) : availableSlots.length === 0 ? (
                        <p className="text-center text-gray-500 py-8">No available times for this date</p>
                      ) : (
                        <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                          {availableSlots.map((slot, index) => {
                            // Support both v1 format (slot is string or has .time) and v2 format (has .startTime)
                            const slotTime = typeof slot === 'string' ? slot : (slot.displayTime || slot.time || slot.startTime || '');
                            const isAvailable = typeof slot === 'string' ? true : slot.available !== false;
                            const isSelected = bookingData.selectedTime === slotTime;
                            
                            return (
                              <button
                                key={slotTime || index}
                                onClick={() => isAvailable ? handleTimeSelect(slotTime) : null}
                                disabled={!isAvailable}
                                className={`
                                  py-3 rounded-lg text-sm font-semibold transition-all
                                  ${!isAvailable 
                                    ? 'bg-gray-100 text-gray-400 line-through cursor-not-allowed border border-gray-200'
                                    : isSelected
                                      ? 'text-white'
                                      : 'border border-gray-300 text-gray-900'
                                  }
                                `}
                                style={
                                  isSelected && isAvailable 
                                    ? { backgroundColor: orgBranding.primaryColor } 
                                    : isAvailable 
                                      ? { '--hover-border': orgBranding.primaryColor }
                                      : {}
                                }
                                onMouseEnter={(e) => isAvailable && !isSelected && (e.target.style.borderColor = orgBranding.primaryColor)}
                                onMouseLeave={(e) => isAvailable && !isSelected && (e.target.style.borderColor = '#d1d5db')}
                              >
                                {slotTime}
                                {!isAvailable && (
                                  <span className="block text-xs mt-1">Booked</span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button
                    onClick={() => goToStep(1)}
                    variant="outline"
                    className="flex-1"
                  >
                    <ChevronLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={() => goToStep(3)}
                    className="flex-1 text-white"
                    style={{ backgroundColor: orgBranding.primaryColor }}
                  >
                    Continue
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Custom Questions & Add-ons */}
            {currentStep === 3 && (
              <div>
                <h2 className="font-bold text-2xl text-gray-900 mb-6">Additional Information</h2>

                {/* Custom Questions */}
                {selectedService.customQuestions && selectedService.customQuestions.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">Questions</h3>
                    <div className="space-y-4">
                      {selectedService.customQuestions.map((question, idx) => (
                        <div key={idx}>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {question.question}
                            {question.required && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          {question.type === 'textarea' ? (
                            <Textarea
                              value={bookingData.customAnswers[question.id] || ''}
                              onChange={(e) => setBookingData(prev => ({
                                ...prev,
                                customAnswers: { ...prev.customAnswers, [question.id]: e.target.value }
                              }))}
                              placeholder="Your answer..."
                              rows={3}
                            />
                          ) : (
                            <Input
                              value={bookingData.customAnswers[question.id] || ''}
                              onChange={(e) => setBookingData(prev => ({
                                ...prev,
                                customAnswers: { ...prev.customAnswers, [question.id]: e.target.value }
                              }))}
                              placeholder="Your answer..."
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Add-ons */}
                {selectedService.addOns && selectedService.addOns.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">Available Add-Ons</h3>
                    <div className="space-y-3">
                      {selectedService.addOns.map(addOn => {
                        const selected = bookingData.selectedAddOns.find(a => a.id === addOn.id);
                        return (
                          <div key={addOn.id} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-semibold text-gray-900">{addOn.name}</h4>
                                  <span className="text-sm font-semibold" style={{ color: orgBranding.primaryColor }}>
                                    +${(addOn.price || 0).toFixed(2)}
                                  </span>
                                </div>
                                <ExpandableDescription description={addOn.description} maxLength={80} />
                              </div>
                              <button
                                onClick={() => handleAddOnToggle(addOn)}
                                className={`
                                  ml-4 px-4 py-2 rounded-lg text-sm font-semibold transition-all
                                  ${selected ? 'text-white' : 'border border-gray-300 text-gray-900'}
                                `}
                                style={selected ? { backgroundColor: orgBranding.primaryColor } : {}}
                              >
                                {selected ? 'Added' : 'Add'}
                              </button>
                            </div>
                            
                            {selected && (
                              <div className="flex items-center gap-3 mt-3">
                                <span className="text-sm text-gray-600">Quantity:</span>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => updateAddOnQuantity(addOn.id, -1)}
                                    className="w-8 h-8 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                                  >
                                    <Minus className="w-4 h-4" />
                                  </button>
                                  <span className="w-8 text-center font-semibold">{selected.quantity}</span>
                                  <button
                                    onClick={() => updateAddOnQuantity(addOn.id, 1)}
                                    className="w-8 h-8 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-50"
                                  >
                                    <Plus className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Special Requests */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Special Requests (Optional)</label>
                  <Textarea
                    value={bookingData.specialRequests}
                    onChange={(e) => setBookingData(prev => ({ ...prev, specialRequests: e.target.value }))}
                    placeholder="Any special requests or notes..."
                    rows={3}
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button
                    onClick={() => goToStep(2)}
                    variant="outline"
                    className="flex-1"
                  >
                    <ChevronLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={() => goToStep(4)}
                    className="flex-1 text-white"
                    style={{ backgroundColor: orgBranding.primaryColor }}
                  >
                    Continue
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 4: Confirmation */}
            {currentStep === 4 && (
              <div>
                <h2 className="font-bold text-2xl text-gray-900 mb-6">Confirm Your Booking</h2>

                {/* Booking Summary */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 mb-6">
                  <div className="space-y-4">
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Service</div>
                      <p className="font-bold text-lg text-gray-900">{selectedService.name}</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Date</div>
                        <p className="font-semibold text-gray-900">
                          {bookingData.selectedDate?.toLocaleDateString('en-US', { 
                            weekday: 'short', month: 'short', day: 'numeric' 
                          })}
                        </p>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Time</div>
                        <p className="font-semibold text-gray-900">{bookingData.selectedTime}</p>
                      </div>
                    </div>

                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                        {selectedService.bookingMode === 'per_ticket' ? 'Tickets' : 'Party Size'}
                      </div>
                      <p className="font-semibold text-gray-900">
                        {selectedService.bookingMode === 'per_ticket' 
                          ? `${bookingData.ticketCount} ticket${bookingData.ticketCount > 1 ? 's' : ''}`
                          : `${bookingData.partySize} guest${bookingData.partySize > 1 ? 's' : ''}`
                        }
                      </p>
                    </div>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 space-y-1 text-sm">
                  <p className="font-semibold text-gray-900">{bookingData.customerName}</p>
                  <p style={{ color: orgBranding.primaryColor }}>{bookingData.customerEmail}</p>
                  <p className="text-gray-600">{bookingData.customerPhone}</p>
                </div>

                {/* Add-ons Summary */}
                {bookingData.selectedAddOns.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Add-Ons</h3>
                    <div className="space-y-2">
                      {bookingData.selectedAddOns.map(addOn => (
                        <div key={addOn.id} className="flex justify-between text-sm">
                          <span className="text-gray-900">
                            {addOn.name} x{addOn.quantity}
                          </span>
                          <span className="font-semibold text-gray-900">
                            ${((addOn.price || 0) * (addOn.quantity || 1)).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Price Breakdown */}
                <div className="border border-gray-200 rounded-lg p-5 mb-6 bg-gray-50">
                  <div className="space-y-3">
                    {/* Subtotal */}
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-700">Service Price</span>
                      <span className="font-semibold text-gray-900">${calculateFees().subtotal.toFixed(2)}</span>
                    </div>
                    
                    {/* Platform Fee */}
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">Platform Fee (3%)</span>
                      <span className="text-gray-900">${calculateFees().platformFee.toFixed(2)}</span>
                    </div>
                    
                    {/* Stripe Fee */}
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">Processing Fee (2.9% + $0.30)</span>
                      <span className="text-gray-900">${calculateFees().stripeFee.toFixed(2)}</span>
                    </div>
                    
                    {/* Divider */}
                    <div className="border-t border-gray-300 pt-3">
                      <div className="flex justify-between items-center">
                        <span className="text-base font-semibold text-gray-700">Total</span>
                        <span className="text-3xl font-bold" style={{ color: orgBranding.primaryColor }}>
                          ${calculateFees().grandTotal.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button
                    onClick={() => goToStep(3)}
                    variant="outline"
                    className="flex-1"
                  >
                    <ChevronLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={submitBooking}
                    className="flex-1 text-white font-semibold py-6 text-lg"
                    style={{ backgroundColor: orgBranding.primaryColor }}
                  >
                    Proceed to Payment
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer - Powered by BookingFlows or © OrgName */}
        <div className="text-center mt-6">
          <a 
            href={orgBranding.showPoweredByBookingFlows ? 'https://bookingflows.com' : (orgBranding.companyWebsite || '#')} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-xs text-gray-500 hover:text-gray-700 transition-colors inline-flex items-center gap-1"
          >
            {orgBranding.showPoweredByBookingFlows ? (
              <>Powered by <span className="font-semibold" style={{ color: orgBranding.primaryColor }}>BookingFlows</span></>
            ) : (
              <>© <span className="font-semibold" style={{ color: orgBranding.primaryColor }}>{orgBranding.name}</span></>
            )}
          </a>
        </div>
      </div>
    </div>
  );
};

export default CustomerBookingWidget;
